import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ClipboardCheck, BarChart3, ShieldCheck, X, ZoomIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CtaLink } from "@/components/landing/CtaLink";
import productScreenshot from "@/assets/product-demo.png";

const callouts = [
  { icon: <ClipboardCheck className="w-5 h-5" />, label: "Requirement tracking" },
  { icon: <BarChart3 className="w-5 h-5" />, label: "Section progress" },
  { icon: <ShieldCheck className="w-5 h-5" />, label: "Quality scoring" },
];

export function ProductDemoSection() {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  return (
    <section id="product" className="pt-[112px] md:pt-[128px] lg:pt-[144px] pb-[80px] md:pb-[100px] lg:pb-[120px] scroll-mt-16">
      <div className="max-w-[1360px] mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 md:mb-20"
        >
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="text-sm font-normal tracking-[0.12em] uppercase text-muted-foreground">In the Workspace</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight leading-[1.15]">
            From upload to AI draft,{" "}<span className="text-white/40 font-normal">in one place.</span>
          </h2>
          
          <p className="mt-8 text-base md:text-lg font-normal text-white/55 max-w-xl mx-auto leading-[1.75]">
            Upload your RFP, let AI extract every requirement, and generate a structured first draft — powered by your own Knowledge Vault.
          </p>
        </motion.div>

        {/* Screenshot */}
        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative mb-6 md:mb-8"
        >
          <p className="text-center text-xs text-white/30 mb-3 md:hidden">Tap to enlarge</p>
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            className="relative mx-auto overflow-hidden rounded-lg cursor-zoom-in group block w-full text-left md:[transform:perspective(1000px)_rotateX(5deg)_rotateY(-5deg)]"
            style={{
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 0 30px rgba(194, 84, 26, 0.15)',
            }}
          >
            <img 
              src={productScreenshot} 
              alt="Sorvaine bid and tender management software dashboard" 
              className="w-full h-auto block"
              loading="lazy"
            />
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-2 text-white/90 text-sm font-medium bg-black/50 px-4 py-2 rounded-full backdrop-blur-sm">
                <ZoomIn className="w-4 h-4" /> Click to enlarge
              </span>
            </div>
            {/* Subtle bottom fade */}
            <div 
              className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none"
              style={{
                background: 'linear-gradient(to bottom, transparent, hsl(var(--background) / 0.5))',
              }}
            />
          </button>
        </motion.div>

        {/* Callouts */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.12, delayChildren: 0.4 } },
          }}
          className="flex flex-col md:flex-row items-start md:items-center justify-center gap-3 md:gap-12 mb-8 md:mb-10 mx-auto w-fit"
        >
          {callouts.map((c) => (
            <motion.div
              key={c.label}
              variants={{
                hidden: { opacity: 0, y: 16 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
              }}
              className="flex items-center gap-3 text-white/80 md:min-w-[200px]"
            >
              <span className="text-primary/70 inline-flex items-center justify-center w-5 h-5 shrink-0 leading-none">{c.icon}</span>
              <span className="text-base md:text-lg font-medium tracking-wide leading-none">{c.label}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center mt-4"
        >
          <CtaLink to="/signup" cta="get_started" location="product_demo">
            <Button className="rounded-full bg-primary hover:bg-primary/90 px-8 text-black">
              Get started
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </CtaLink>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={false}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 md:p-8"
            onClick={() => setLightboxOpen(false)}
          >
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 md:top-6 md:right-6 text-white/70 hover:text-white transition-colors z-10"
            >
              <X className="w-6 h-6 md:w-8 md:h-8" />
            </button>
            <motion.img
              initial={false}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              src={productScreenshot}
              alt="Sorvaine bid and tender management software dashboard — enlarged view"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
